import type { PdfAnnotation, PlanReview } from '../types';

export interface PlanReviewSummaryItem {
  annotationId: string;
  pageNumber: number;
  authorName: string;
  content: string;
  replyCount: number;
  contextPreviewUrl?: string;
}

export interface PlanReviewPageGroup {
  pageNumber: number;
  items: PlanReviewSummaryItem[];
}

/** Group plan-review pins by page for digest emails / in-app summary. */
export function groupPlanReviewAnnotationsByPage(
  annotations: Array<
    Pick<
      PdfAnnotation,
      'id' | 'pageNumber' | 'userName' | 'content' | 'replies' | 'contextPreviewUrl'
    >
  >,
): PlanReviewPageGroup[] {
  const byPage = new Map<number, PlanReviewSummaryItem[]>();
  for (const ann of annotations) {
    const page = Number(ann.pageNumber) || 1;
    const item: PlanReviewSummaryItem = {
      annotationId: ann.id || '',
      pageNumber: page,
      authorName: ann.userName || 'Someone',
      content: (ann.content || '').trim() || '(no text)',
      replyCount: Array.isArray(ann.replies) ? ann.replies.length : 0,
      contextPreviewUrl: ann.contextPreviewUrl,
    };
    const list = byPage.get(page) || [];
    list.push(item);
    byPage.set(page, list);
  }
  return Array.from(byPage.entries())
    .sort(([a], [b]) => a - b)
    .map(([pageNumber, items]) => ({ pageNumber, items }));
}

/** Collect @mention user ids from pins in a review slice (excludes `excludeUserId`). */
export function collectPlanReviewMentionUserIds(
  annotations: Array<Pick<PdfAnnotation, 'mentions' | 'replies'>>,
  excludeUserId?: string,
): string[] {
  const ids = new Set<string>();
  const exclude = (excludeUserId || '').trim();
  for (const ann of annotations) {
    if (Array.isArray(ann.mentions)) {
      for (const m of ann.mentions) {
        if (m?.userId && m.userId !== exclude) ids.add(m.userId);
      }
    }
    if (Array.isArray(ann.replies)) {
      for (const r of ann.replies) {
        if (Array.isArray(r.mentions)) {
          for (const m of r.mentions) {
            if (m?.userId && m.userId !== exclude) ids.add(m.userId);
          }
        }
      }
    }
  }
  return Array.from(ids);
}

export function buildPlanReviewDefaultNotifyUserIds(params: {
  uploaderUserId?: string;
  designerUserId?: string;
  mentionUserIds?: string[];
  projectManagerUserIds?: string[];
  excludeUserId?: string;
}): string[] {
  const exclude = (params.excludeUserId || '').trim();
  const ids = new Set<string>();
  const add = (uid?: string) => {
    const t = (uid || '').trim();
    if (t && t !== exclude) ids.add(t);
  };
  add(params.uploaderUserId);
  add(params.designerUserId);
  for (const uid of params.mentionUserIds || []) add(uid);
  for (const uid of params.projectManagerUserIds || []) add(uid);
  return Array.from(ids);
}

/** Plain-text summary body for Slack / email fallback. */
export function formatPlanReviewSummaryText(
  groups: PlanReviewPageGroup[],
  options?: { fileName?: string; summaryNote?: string; markupStrokeCount?: number },
): string {
  const lines: string[] = [];
  if (options?.fileName) lines.push(`File: ${options.fileName}`, '');
  if (options?.summaryNote?.trim()) {
    lines.push('Note:', options.summaryNote.trim(), '');
  }
  if (!groups.length) {
    lines.push('No PDF comments were added during this review.');
  } else {
    for (const g of groups) {
      lines.push(`Page ${g.pageNumber}:`);
      for (const item of g.items) {
        const replies =
          item.replyCount > 0
            ? ` (${item.replyCount} ${item.replyCount === 1 ? 'reply' : 'replies'})`
            : '';
        lines.push(`- ${item.authorName}: ${item.content}${replies}`);
      }
      lines.push('');
    }
  }
  if (options?.markupStrokeCount && options.markupStrokeCount > 0) {
    lines.push(
      `Markup: ${options.markupStrokeCount} stroke${options.markupStrokeCount === 1 ? '' : 's'}`,
    );
  }
  return lines.join('\n').trim();
}

export type VersionPlanReviewState = {
  active: PlanReview | null;
  /** Most recently completed review for this document version. */
  latestCompleted: PlanReview | null;
};

/** Collapse stream reviews into per-document active + latest-completed state. */
export function buildVersionPlanReviewMap(
  reviews: PlanReview[],
): Map<string, VersionPlanReviewState> {
  const map = new Map<string, VersionPlanReviewState>();
  for (const review of reviews) {
    const docId = (review.documentId || '').trim();
    if (!docId) continue;
    const cur = map.get(docId) || { active: null, latestCompleted: null };
    if (review.status === 'active') {
      if (
        !cur.active ||
        review.startedAt.getTime() > cur.active.startedAt.getTime()
      ) {
        cur.active = review;
      }
    } else if (review.status === 'completed') {
      const completedAt = review.completedAt?.getTime() ?? review.startedAt.getTime();
      const prevAt =
        cur.latestCompleted?.completedAt?.getTime() ??
        cur.latestCompleted?.startedAt.getTime() ??
        0;
      if (!cur.latestCompleted || completedAt > prevAt) {
        cur.latestCompleted = review;
      }
    }
    map.set(docId, cur);
  }
  return map;
}
